import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import type { Document } from 'models/document/document';
import type { FC } from 'react';
import { useMemo } from 'react';

export interface DocumentSelectorProps {
  value: number[];
  onChange: (newValue: number[]) => void;
  allDocuments: Document[] | undefined;
  isLoading: boolean;
}

const DocumentSelector: FC<DocumentSelectorProps> = ({
  value,
  onChange,
  allDocuments,
  isLoading,
}) => {
  const selectedDocuments = useMemo(() => {
    if (!allDocuments) return [];
    return allDocuments.filter((doc) => value.includes(doc.id));
  }, [allDocuments, value]);

  return (
    <Stack direction='row' alignItems='center'>
      <Autocomplete
        multiple
        disabled={isLoading}
        options={allDocuments ?? []}
        value={selectedDocuments}
        getOptionLabel={(doc) => doc.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={isLoading}
        onChange={(_, newValue) => {
          onChange(newValue.map((doc) => doc.id));
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Доступные документы'
            margin='normal'
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox checked={selected} />
            {option.name}
          </li>
        )}
        fullWidth
      />
      <Tooltip title='Документы, доступные сотруднику в заказе' describeChild>
        <IconButton
          sx={{ mt: 1, ml: 0.5 }}
          size='small'
          edge='end'
          aria-label='Подробнее о документах сотрудника'>
          <InfoOutlinedIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export default DocumentSelector;
